/**
 * Dual-gender sizes inside the Reelfy video popup ("From Our Community").
 *
 * Reelfy renders one radio group per product option ("Size" and
 * "Target gender"), so a shopper can only resolve a single variant — either
 * the men's piece or the women's piece, never both. Our couple sets are sold
 * as one unit at one price, so the popup has to behave like the product page:
 * pick a Men's size AND a Women's size, then add a single line item carrying
 * both sizes as line-item properties.
 *
 * Reelfy internals this relies on (reelfy.js, app embed):
 *   - `reelfyProductForm` document event: {product, sectionId, form, variants}
 *   - `.rf-product-variants` holds the option fieldsets
 *   - `.rf-addtocart-form > form` holds `input[name="id"]` + the ATC button
 *   - `window.rfProducts[id]` caches the product JSON
 */
(function () {
  "use strict";

  var MENS = "Men's Size";
  var WOMENS = "Women's Size";
  var uid = 0;
  /** `.lvd-rf-dual-variants` element -> its `.rf-addtocart-form` wrapper. */
  var wrapperFor = new WeakMap();

  function normalize(value) {
    return String(value == null ? "" : value)
      .trim()
      .toLowerCase();
  }

  function getOptions(product) {
    var opts = (window.rfProductOptions && window.rfProductOptions[product.id]) || product.options;
    if (!opts || !opts.length) return [];
    // Older product JSON exposes options as plain strings.
    return opts.map(function (opt, i) {
      if (typeof opt === "string") return { name: opt, position: i + 1 };
      return { name: opt.name, position: opt.position || i + 1 };
    });
  }

  /** Position (1-based) of the Size / Target gender options, or 0 when absent. */
  function getDualPositions(product) {
    var sizePos = 0;
    var genderPos = 0;
    getOptions(product).forEach(function (opt) {
      var name = normalize(opt.name);
      if (name === "size") sizePos = opt.position;
      if (name === "target gender") genderPos = opt.position;
    });
    return { size: sizePos, gender: genderPos };
  }

  function variantOption(variant, position) {
    if (position === 1) return variant.option1;
    if (position === 2) return variant.option2;
    if (position === 3) return variant.option3;
    return null;
  }

  /**
   * Collect the distinct sizes offered for each gender, in variant order.
   * @returns {{mens: Array, womens: Array}} entries of {size, variantId, available}
   */
  function collectSizes(product, positions) {
    var mens = [];
    var womens = [];
    var seen = { male: {}, female: {} };

    (product.variants || []).forEach(function (variant) {
      var size = String(variantOption(variant, positions.size) || "").trim();
      var gender = normalize(variantOption(variant, positions.gender));
      if (!size || (gender !== "male" && gender !== "female")) return;
      if (seen[gender][size]) return;
      seen[gender][size] = true;
      (gender === "male" ? mens : womens).push({
        size: size,
        variantId: variant.id,
        available: !!variant.available,
      });
    });

    return { mens: mens, womens: womens };
  }

  function escapeAttr(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function buildGroup(legend, groupName, entries) {
    if (!entries.length) return "";
    var html = '<legend class="rf-form__label">' + escapeAttr(legend) + "</legend>";
    entries.forEach(function (entry, index) {
      var id = groupName + "-" + index;
      html +=
        '<input type="radio" class="lvd-rf-size-radio rfHidden" id="' +
        id +
        '" name="' +
        groupName +
        '" value="' +
        escapeAttr(entry.size) +
        '" data-variant-id="' +
        entry.variantId +
        '"' +
        (entry.available ? "" : " disabled") +
        ">";
      html +=
        '<label class="lvd-rf-size-label" for="' +
        id +
        '">' +
        escapeAttr(entry.size) +
        "</label>";
    });
    return '<fieldset class="rf-product-form__input lvd-rf-size-group">' + html + "</fieldset>";
  }

  /**
   * Replace Reelfy's option radios with paired Men's / Women's size pickers.
   * Every wrapper we look at gets marked so the DOM sweep never reconsiders it:
   * "1" = enhanced, "0" = not a dual-gender product.
   */
  function enhance(product, variantsEl, formEl) {
    if (!product || !formEl) return;
    var wrapper = formEl.querySelector(".rf-addtocart-form");
    if (!wrapper || wrapper.dataset.lvdDual) return;

    var positions = getDualPositions(product);
    var sizes = positions.size && positions.gender ? collectSizes(product, positions) : null;
    if (!variantsEl || !sizes || !sizes.mens.length || !sizes.womens.length) {
      wrapper.dataset.lvdDual = "0";
      return;
    }

    uid += 1;
    var prefix = "lvd-rf-" + product.id + "-" + uid;

    variantsEl.innerHTML =
      buildGroup(MENS, prefix + "-mens", sizes.mens) +
      buildGroup(WOMENS, prefix + "-womens", sizes.womens) +
      '<div class="lvd-rf-size-note" hidden>Select both a Men\'s and a Women\'s size.</div>';
    variantsEl.id = prefix + "-variants";
    variantsEl.classList.remove("rfHidden");
    variantsEl.classList.add("lvd-rf-dual-variants");
    variantsEl.dataset.lvdMensName = prefix + "-mens";
    variantsEl.dataset.lvdWomensName = prefix + "-womens";

    wrapper.dataset.lvdDual = "1";
    wrapper.dataset.lvdVariants = variantsEl.id;
    wrapperFor.set(variantsEl, wrapper);
  }

  function findVariantsEl(wrapper) {
    return document.getElementById(wrapper.dataset.lvdVariants || "");
  }

  function getSelection(wrapper) {
    var variantsEl = findVariantsEl(wrapper);
    if (!variantsEl) return { mens: null, womens: null };
    return {
      mens: variantsEl.querySelector(
        'input[name="' + variantsEl.dataset.lvdMensName + '"]:checked'
      ),
      womens: variantsEl.querySelector(
        'input[name="' + variantsEl.dataset.lvdWomensName + '"]:checked'
      ),
    };
  }

  function closeReelfyPopup() {
    var close = document.querySelector(".pswp_rf--open .pswp_rf__button--close");
    if (close) close.click();

    var mobile = document.getElementById("rf-mobile-product");
    if (mobile) mobile.classList.remove("active");
  }

  function toast() {
    var utils = window.rfUtils;
    if (!utils || typeof utils.showToast !== "function") return;
    var message =
      typeof utils.getCurrentLange === "function"
        ? utils.getCurrentLange("added_to_cart")
        : "Added to cart";
    utils.showToast(message || "Added to cart");
  }

  function showNote(wrapper) {
    var variantsEl = findVariantsEl(wrapper);
    var note = variantsEl && variantsEl.querySelector(".lvd-rf-size-note");
    if (note) note.hidden = false;

    // Reuse the site-wide "Select Both Sizes" dialog when it is on the page.
    var popup = document.getElementById("lvd-size-popup");
    if (popup) popup.classList.add("show");
  }

  function hideNote(variantsEl) {
    var note = variantsEl && variantsEl.querySelector(".lvd-rf-size-note");
    if (note) note.hidden = true;
  }

  /**
   * Reelfy ships the error container but never the `.rf-error-message` node it
   * writes into, so create it on demand.
   */
  function showError(wrapper, data) {
    var box = wrapper.querySelector(".rfProduct__error-message-wrapper");
    if (!box) return;
    var slot = box.querySelector(".rf-error-message");
    if (!slot) {
      slot = document.createElement("span");
      slot.className = "rf-error-message";
      box.appendChild(slot);
    }
    slot.textContent =
      (data && (data.description || data.message)) ||
      "Sorry, this set could not be added to your cart. Please try again.";
    box.classList.remove("rfHidden");
  }

  function hideError(wrapper) {
    var box = wrapper.querySelector(".rfProduct__error-message-wrapper");
    if (box) box.classList.add("rfHidden");
  }

  function setLoading(btn, loading) {
    var spinner = btn.querySelector(".rf-loading-overlay__spinner");
    if (loading) {
      btn.classList.add("loading");
      if (spinner) spinner.classList.remove("rfHidden");
    } else {
      btn.classList.remove("loading");
      if (spinner) spinner.classList.add("rfHidden");
    }
  }

  function flashAdded(btn) {
    var check = btn.querySelector(".rf-loading-overlay__check");
    btn.classList.add("added");
    if (check) check.classList.remove("rfHidden");
    setTimeout(function () {
      btn.classList.remove("added");
      if (check) check.classList.add("rfHidden");
    }, 2000);
  }

  function onAddToCartClick(ev) {
    var btn = ev.target && ev.target.closest && ev.target.closest(".rf-button-atc");
    if (!btn) return;

    var wrapper = btn.closest('.rf-addtocart-form[data-lvd-dual="1"]');
    if (!wrapper) return;

    // Reelfy binds its own handler on the button itself; stop the event here so
    // it never runs and we own the request.
    ev.preventDefault();
    ev.stopImmediatePropagation();

    if (btn.classList.contains("disabled") || btn.classList.contains("loading")) return;

    var selection = getSelection(wrapper);
    if (!selection.mens || !selection.womens) {
      showNote(wrapper);
      return;
    }

    // One line item per set: the men's and women's pieces are separate Shopify
    // variants but the set is one unit at one price, so two lines double-charge.
    var items = [
      {
        id: parseInt(selection.mens.getAttribute("data-variant-id"), 10),
        quantity: 1,
        properties: (function () {
          var props = {};
          props[MENS] = selection.mens.value;
          props[WOMENS] = selection.womens.value;
          return props;
        })(),
      },
    ];

    if (!items[0].id) {
      showError(wrapper, { message: "Could not resolve that size combination. Please refresh and try again." });
      return;
    }

    hideError(wrapper);
    setLoading(btn, true);

    var post =
      (window.LVD && window.LVD.postDualCartItems) ||
      function () {
        return Promise.reject(new Error("LVD.postDualCartItems unavailable"));
      };

    // Dismiss the video lightbox once the add succeeds but before the theme
    // renders its cart drawer, otherwise the drawer opens under the overlay.
    post(items, closeReelfyPopup)
      .then(function (result) {
        if (result && result.ok) {
          flashAdded(btn);
          toast();
        } else {
          showError(wrapper, result && result.data);
        }
      })
      .catch(function (error) {
        console.error("[LVD] Reelfy dual-size add to cart failed", error);
        showError(wrapper, null);
      })
      .finally(function () {
        setLoading(btn, false);
      });
  }

  function onSizeChange(ev) {
    var input = ev.target;
    if (!input || !input.classList || !input.classList.contains("lvd-rf-size-radio")) return;

    var variantsEl = input.closest(".lvd-rf-dual-variants");
    hideNote(variantsEl);

    // Keep Reelfy's hidden variant field pointing at a real variant, so any
    // code path we don't own still posts something valid.
    var wrapper = variantsEl && wrapperFor.get(variantsEl);
    var idField = wrapper && wrapper.querySelector('input[name="id"]');
    var mens = wrapper && getSelection(wrapper).mens;
    if (idField && mens) idField.value = mens.getAttribute("data-variant-id");
  }

  /**
   * Reelfy dispatches `reelfyProductForm` synchronously from its render call,
   * and binds its own handlers from a listener registered at app-embed load
   * time. Defer a tick so we always rewrite the markup after that, whichever
   * script happened to load first.
   */
  document.addEventListener("reelfyProductForm", function (ev) {
    var detail = ev.detail || {};
    setTimeout(function () {
      enhance(detail.product, detail.variants, detail.form);
    }, 0);
  });

  document.addEventListener("click", onAddToCartClick, true);
  document.addEventListener("change", onSizeChange);

  // Fallback for popups rendered before this script runs (deferred asset) or
  // re-rendered without a fresh event.
  function sweep() {
    document.querySelectorAll(".rf-addtocart-form:not([data-lvd-dual])").forEach(function (wrap) {
      var productId = wrap.getAttribute("data-product");
      var product = window.rfProducts && window.rfProducts[productId];
      if (!product) return;

      var host = wrap.closest(".rf-product-form") || wrap.parentElement;
      var node = host;
      var variantsEl = null;
      while (node && node !== document.body && !variantsEl) {
        variantsEl = node.querySelector(".rf-product-variants");
        node = node.parentElement;
      }
      enhance(product, variantsEl, host);
    });
  }

  function startSweeping() {
    var queued = false;
    sweep();
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        sweep();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startSweeping);
  } else {
    startSweeping();
  }
})();
